import React, { useRef, useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { v4 as uuid } from "uuid";
import flat from "flat";
import { requestContact, requestCrmcontact } from "../../tide-api";
import { formatDate, processPlaceholders } from "../../utils";
import { TideAccommodation, TideRegime } from "../../types";
import { compact, get, head, isEmpty, isNil, range, set, values } from "lodash";
import LabeledInput from "../labeled-input";
import LabeledTextArea from "../labeled-text-area";
import LabeledCheckbox from "../labeled-checkbox";
import LabeledSelect from "../labeled-select";
import produce from "immer";
import _ from "lodash";
import { format, addDays, addHours, isBefore, isFuture, isValid } from "date-fns";
import { useQSMStore } from "../../app/search-results/qsm-store/context";
import { sortProps } from "../../app/search-results/qsm/utils";
import { Room } from "../../app/search-results/qsm/room-picker";
import { useI18next } from "gatsby-plugin-react-i18next";
import { ContactFormRequest, CrmContactRequest } from "@qite/tide-client";

interface OfferFormProps {
  title?: string;
  introText?: string;
  emailSubject?: string;
  emailMessage?: string;
  newsletterTag?: number;
  offerTag?: number;
  productName?: string;
  productCode?: string;
  minDuration?: number;
  minDurationType?: "hours" | "days" | "nights";
  accommodations?: TideAccommodation[];
  regimes?: TideRegime[];
  confirmationEmailTemplate?: number;
  privacyPath?: string;
  privacyText?: string;
  confirmationTitle?: string;
  confirmationText?: string;
}

interface OfferFormValues {
  firstName: string;
  lastName: string;
  email: string;
  telNr: string;
  departureDate: string;
  retourDate: string;
  message: string;
  adultCount: number;
  childCount: number;
  children: { guid: string; age?: number }[];
  gender: string;
  acceptTerms: boolean;
  newsletter: boolean;
  flightIncluded: boolean;
  accommodation: string;
  regime: string;
}

const OfferForm: React.FC<OfferFormProps> = ({
  title,
  introText,
  emailSubject,
  emailMessage,
  confirmationEmailTemplate,
  newsletterTag,
  offerTag,
  productName,
  productCode,
  minDuration,
  minDurationType,
  accommodations,
  regimes,
  privacyPath,
  privacyText,
  confirmationTitle,
  confirmationText,
}) => {
  const [qsmState] = useQSMStore();

  const formRef = useRef<HTMLDivElement>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  // Synchronous guard: state updates are batched, so a burst of submits (rapid clicks, or a
  // held enter key repeating) would all pass a `submitting` check and each send its own mail.
  const submitInFlight = useRef(false);
  const { t, language } = useI18next();

  const formik = useFormik<OfferFormValues>({
    initialValues: {
      gender: "",
      firstName: "",
      lastName: "",
      email: "",
      telNr: "",
      departureDate: qsmState?.dateRange.fromDate
        ? formatDate(qsmState?.dateRange.fromDate, "yyyy-MM-dd")
        : "",
      retourDate: qsmState?.dateRange.toDate
        ? formatDate(qsmState?.dateRange.toDate, "yyyy-MM-dd")
        : "",
      adultCount: qsmState?.rooms.reduce((total, room) => total + room.adults, 0) ?? 1,
      childCount: qsmState?.rooms.reduce((total, room) => total + room.children, 0) ?? 0,
      children:
        qsmState?.rooms.flatMap((room) => room.childAges.map((age) => ({ guid: uuid(), age }))) ??
        [],
      accommodation:
        accommodations
          ?.find((o) => o.code === qsmState?.rooms[0]?.accommodationCode)
          ?.tideId.toString() ?? "",
      regime:
        regimes?.find((r) => r.code === qsmState?.rooms[0]?.regimeCode)?.tideId.toString() ?? "",
      message: "",
      flightIncluded: true,
      newsletter: false,
      acceptTerms: false,
    },
    validate: (values) => {
      const errors: Record<string, string | Record<string, string>> = {};

      if (isEmpty(values.gender)) {
        errors.gender = t("PLEASE_ENTER_GENDER");
      }

      if (isEmpty(values.firstName)) {
        errors.firstName = t("PLEASE_ENTER_FIRST_NAME");
      }

      if (isEmpty(values.lastName)) {
        errors.lastName = t("PLEASE_ENTER_LAST_NAME");
      }

      try {
        Yup.string()
          .required(t("PLEASE_ENTER_EMAIL"))
          .email(t("PLEASE_ENTER_VALID_EMAIL"))
          .validateSync(values.email);
      } catch (err: any) {
        errors.email = head(err.errors) ?? "";
      }

      if (isEmpty(values.telNr)) {
        errors.telNr = t("PLEASE_ENTER_PHONE");
      }

      if (isEmpty(values.departureDate)) {
        errors.departureDate = t("PLEASE_ENTER_DEPARTURE_DATE");
      } else if (!isValid(new Date(values.departureDate))) {
        errors.departureDate = t("PLEASE_ENTER_VALID_DEPARTURE_DATE");
      } else if (!isFuture(new Date(values.departureDate))) {
        errors.departureDate = t("PLEASE_ENTER_DEPARTURE_DATE_IN_FUTURE");
      }

      if (isEmpty(values.retourDate)) {
        errors.retourDate = t("PLEASE_ENTER_RETURN_DATE");
      } else if (!isValid(new Date(values.departureDate))) {
        errors.retourDate = t("PLEASE_ENTER_VALID_RETURN_DATE");
      } else {
        const min = minDuration ?? 0;
        const dateISO = new Date(values.departureDate);
        const earliestReturnDate =
          minDurationType === "hours"
            ? addHours(dateISO, min)
            : minDurationType === "days"
            ? addDays(dateISO, min > 0 ? min - 1 : min)
            : minDurationType === "nights"
            ? addDays(dateISO, min)
            : dateISO;

        if (isBefore(new Date(values.retourDate), earliestReturnDate)) {
          errors.retourDate = t("EARLIEST_RETURN_DATE_FOR_DEPARTURE_DATE_IS_X", {
            earliestReturnDate: format(earliestReturnDate, "dd/MM/yyyy"),
          });
        }
      }

      if (isNil(values.adultCount)) {
        errors.adultCount = t("PLEASE_ENTER_NUMBER_OF_ADULTS");
      } else if (values.adultCount < 1) {
        errors.adultCount = t("NUMBER_OF_ADULTS_SHOULD_BE_AT_LEAST_1");
      }

      if (isNil(values.childCount)) {
        errors.childCount = t("PLEASE_ENTER_NUMBER_OF_CHILDREN");
      } else if (values.adultCount < 0) {
        errors.childCount = t("NUMBER_OF_CHILDREN_CAN_NOT_BE_NEGATIVE");
      }

      for (let i = 0; i < values.children.length; i++) {
        if (isNil(get(values, `children.${i}.age`))) {
          set(
            errors,
            `children.${i}.age`,
            t("PLEASE_ENTER_AGE_OF_CHILD_X", { childNumber: i + 1 })
          );
        }
      }

      if (isEmpty(values.accommodation)) {
        errors.accommodation = t("PLEASE_SELECT_ACCOMMODATION");
      }

      if (regimes?.length && isEmpty(values.regime)) {
        errors.regime = t("PLEASE_SELECT_REGIME");
      }

      if (!values.acceptTerms) {
        errors.acceptTerms = t("PLEASE_AGREE_WITH_TERMS");
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (submitInFlight.current) {
        return;
      }

      submitInFlight.current = true;
      setSubmitting(true);
      setSubmitFailed(false);

      try {
        if (isNil(offerTag)) {
          throw Error("offerTag was not supplied");
        }

        if (isNil(newsletterTag)) {
          throw Error("newsletterTag was not supplied");
        }

        if (isNil(emailMessage)) {
          throw Error("emailMessage was not supplied");
        }

        if (isNil(emailSubject)) {
          throw Error("emailSubject was not supplied");
        }

        const tags = compact([offerTag, values.newsletter && newsletterTag]);

        const selectedAccommodation = accommodations?.find(
          (a) => `${a.tideId}` === values.accommodation
        );

        const selectedRegime = regimes?.find((r) => `${r.tideId}` === values.regime);

        const replacements = {
          firstName: values.firstName ?? "",
          lastName: values.lastName ?? "",
          email: values.email ?? "",
          phone: values.telNr ?? "",
          productName: productName ?? "",
          productCode: productCode ?? "",
          message: values.message ?? "",
          adults: values.adultCount.toString() ?? "",
          children: values.childCount.toString() ?? "",
          childAges:
            values.children.length > 0
              ? `(${t("X_YEARS_OLD", { age: values.children.map((c) => c.age).join(", ") })})`
              : "",
          preferences: selectedRegime?.name ?? "",
          departureDate: values.departureDate ?? "",
          returnDate: values.retourDate ?? "",
          acco: selectedAccommodation?.name?.toString() ?? "",
          flightIncluded: values.flightIncluded ? t("FLIGHT_INCLUDED") : t("FLIGHT_NOT_INCLUDED"),
        };

        const subject = processPlaceholders(emailSubject, replacements);
        const message = processPlaceholders(emailMessage, replacements);

        await Promise.all([
          // contact request + confirmation mail
          requestCrmcontact({
            firstName: values.firstName,
            lastName: values.lastName,
            gender: !isEmpty(values.gender) ? parseInt(values.gender) : undefined,
            email: values.email,
            phone: values.telNr,
            remark: values.message,
            contactMessage: values.regime,
            tags: tags,
            tourGroupPreference: {
              adults: values.adultCount,
              children: values.childCount,
              rooms: 1,
            },
            languageCode: language,
            confirmationEmailTemplate: confirmationEmailTemplate,
          } as CrmContactRequest),
          // internal mail
          requestContact({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phone: values.telNr,
            subject: subject,
            message: message,
          } as ContactFormRequest),
        ]);

        setFormSubmitted(true);
        formRef.current?.scrollIntoView();
      } catch (error) {
        console.error("error", error);
        setSubmitFailed(true);
      } finally {
        submitInFlight.current = false;
        setSubmitting(false);
      }
    },
  });

  const handleChildCountChange: React.FormEventHandler<HTMLInputElement> = (e) => {
    const count = parseInt(e.currentTarget.value);
    if (!Number.isNaN(count)) {
      formik.setValues(
        produce(formik.values, (values) => {
          values.childCount = count;
          values.children = range(0, count).map(
            (index) =>
              formik.values.children[index] ?? {
                guid: uuid(),
                age: undefined,
              }
          );
        }),
        false
      );
    }
  };

  // Clear the form on the way back, so the visitor cannot resend an identical request by
  // simply pressing the submit button again. Only the fields identifying the sender are
  // cleared: the trip context is carried over from the current values, because resetting to
  // the initial values would drop whatever the search-state effects filled in after the
  // first render, and those effects do not run again.
  const openForm = () => {
    formik.resetForm({
      values: {
        ...formik.values,
        gender: "",
        firstName: "",
        lastName: "",
        email: "",
        telNr: "",
        message: "",
        newsletter: false,
        acceptTerms: false,
      },
    });
    setFormSubmitted(false);
    setSubmitFailed(false);
  };

  const flatErrors: Record<string, string> = flat(formik.errors);
  const hasVisibleError = (key: string) => get(formik.errors, key) && get(formik.touched, key);

  useEffect(() => {
    formik.setValues(
      produce(formik.values, (values) => {
        values.adultCount = qsmState?.rooms.reduce((total, room) => total + room.adults, 0) ?? 1;
        values.childCount = qsmState?.rooms.reduce((total, room) => total + room.children, 0) ?? 0;
        values.children =
          qsmState?.rooms.flatMap((room) => room.childAges.map((age) => ({ guid: uuid(), age }))) ??
          [];
      }),
      false
    );
  }, [JSON.stringify(qsmState?.rooms.map(sortProps))]);

  useEffect(() => {
    var firstRoom = qsmState?.rooms ? qsmState?.rooms[0] : ({} as Room);

    if (firstRoom.accommodationCode) {
      const accommodationId = accommodations?.find((a) => a.code === firstRoom.accommodationCode)
        ?.tideId;

      if (typeof accommodationId !== "undefined") {
        formik.setValues(
          produce(formik.values, (values) => {
            values.accommodation = accommodationId.toString();
          }),
          true
        );
      }
    }

    if (firstRoom?.regimeCode) {
      const regimeId = regimes?.find((r) => r.code === firstRoom.regimeCode)?.tideId;

      if (typeof regimeId !== "undefined") {
        formik.setValues(
          produce(formik.values, (values) => {
            values.regime = regimeId.toString();
          }),
          true
        );
      }
    }
  }, [qsmState?.rooms]);

  useEffect(() => {
    formik.setValues(
      produce(formik.values, (values) => {
        values.departureDate = qsmState?.dateRange.fromDate
          ? formatDate(qsmState?.dateRange.fromDate, "yyyy-MM-dd")
          : "";
        values.retourDate = qsmState?.dateRange.toDate
          ? formatDate(qsmState?.dateRange.toDate, "yyyy-MM-dd")
          : "";
      }),
      true
    );
  }, [qsmState?.dateRange.fromDate?.valueOf(), qsmState?.dateRange.toDate?.valueOf()]);

  return (
    <section className="section">
      <div className="contact-form" id="offer-form" ref={formRef}>
        <div className="contact-form__form">
          {!formSubmitted ? (
            <div className="form-state">
              <div className="contact-form__intro">
                <h2 className="contact-form__title">{title}</h2>
                <div dangerouslySetInnerHTML={{ __html: introText || "" }}></div>
              </div>
              <form
                noValidate
                className="form"
                onSubmit={formik.handleSubmit}
                name="form--offer"
                id="form--offer"
              >
                <div className="form__region">
                  <div className="form__row">
                    <div
                      className={
                        hasVisibleError("gender") ? "form__group form__group--error" : "form__group"
                      }
                    >
                      <div className="radiobutton-group">
                        <div className="radiobutton">
                          <label className="radiobutton__label">
                            <input
                              type="radio"
                              value="0"
                              name="gender"
                              className="radiobutton__input"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              defaultChecked={formik.values.gender === "0"}
                            />
                            {t("MR")}
                          </label>
                        </div>

                        <div className="radiobutton">
                          <label className="radiobutton__label">
                            <input
                              type="radio"
                              value="1"
                              name="gender"
                              className="radiobutton__input"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              defaultChecked={formik.values.gender === "1"}
                            />
                            {t("MS")}
                          </label>
                        </div>

                        <div className="radiobutton">
                          <label className="radiobutton__label">
                            <input
                              type="radio"
                              value="2"
                              name="gender"
                              className="radiobutton__input"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              defaultChecked={formik.values.gender === "2"}
                            />
                            {t("OTHER")}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form__row">
                    <LabeledInput
                      name="firstName"
                      label={t("FIRST_NAME")}
                      required
                      placeholder={t("FIRST_NAME")}
                      extraClassName="form__group--sm-50"
                      hasError={hasVisibleError("firstName")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.firstName}
                    />
                    <LabeledInput
                      name="lastName"
                      label={t("LAST_NAME")}
                      required
                      placeholder={t("LAST_NAME")}
                      extraClassName="form__group--sm-50"
                      hasError={hasVisibleError("lastName")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.lastName}
                    />
                    <LabeledInput
                      type="email"
                      name="email"
                      label={t("EMAIL")}
                      required
                      placeholder={t("EMAIL_ADDRESS")}
                      extraClassName="form__group--sm-50"
                      hasError={hasVisibleError("email")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                    />
                    <LabeledInput
                      name="telNr"
                      label={t("PHONE")}
                      required
                      placeholder={t("PHONE_NUMBER")}
                      extraClassName="form__group--sm-50"
                      hasError={hasVisibleError("telNr")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.telNr}
                    />
                  </div>
                </div>
                <div className="form__region">
                  <div className="form__row">
                    <LabeledInput
                      type="date"
                      name="departureDate"
                      label={t("PREFERRED_DEPARTURE_DATE")}
                      required
                      placeholder={t("CHOOSE_PREFERRED_DEPARTURE")}
                      extraClassName="form__group--sm-50"
                      hasError={hasVisibleError("departureDate")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.departureDate}
                    />
                    <LabeledInput
                      type="date"
                      name="retourDate"
                      label={t("PREFERRED_RETURN_DATE")}
                      required
                      placeholder={t("SELECT_PREFERRED_RETURN_DATE")}
                      extraClassName="form__group--sm-50"
                      hasError={hasVisibleError("retourDate")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.retourDate}
                    />
                    <LabeledInput
                      type="number"
                      extraClassName="form__group--sm-50 form__group--md-33"
                      name="adultCount"
                      label={t("ADULTS")}
                      min={1}
                      required
                      hasError={hasVisibleError("adultCount")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.adultCount}
                    />
                    <LabeledInput
                      type="number"
                      extraClassName="form__group--sm-50 form__group--md-33"
                      name="childCount"
                      label={t("CHILDREN")}
                      required
                      min={0}
                      hasError={hasVisibleError("childCount")}
                      onChange={handleChildCountChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.childCount}
                    />
                  </div>
                  {(formik.values.children ?? []).map((child, index) => (
                    <div className="form__row" key={child.guid}>
                      <LabeledSelect
                        name={`children.${index}.age`}
                        extraClassName="form__group--sm-50 form__group--md-33"
                        label={t("CHILD_X_AGE_AT_DEPARTURE_DATE", { childNumber: index + 1 })}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        hasError={hasVisibleError(`children.${index}.age`)}
                        options={[
                          { key: "empty", label: "", value: undefined },
                          ...range(0, 18).map((age) => ({
                            key: `${age}`,
                            value: age,
                            label: `${age}`,
                          })),
                        ]}
                        value={Number.isNaN(child.age) ? undefined : child.age}
                      />
                    </div>
                  ))}
                </div>
                <div className="form__region">
                  <div className="form__row">
                    <LabeledSelect
                      name="accommodation"
                      extraClassName="form__group--sm-50 form__group--md-33"
                      label={t("ACCOMMODATION")}
                      required
                      hasError={hasVisibleError("accommodation")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.accommodation}
                      options={[
                        { key: "empty", label: "", value: undefined },
                        ...(accommodations ?? []).map((element) => ({
                          key: element.id,
                          value: element.tideId ?? undefined,
                          label: element.name ?? "",
                        })),
                      ]}
                    />
                    {regimes?.length ? (
                      <LabeledSelect
                        name="regime"
                        extraClassName="form__group--sm-50 form__group--md-33"
                        label={t("REGIME")}
                        required
                        hasError={hasVisibleError("regime")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.regime}
                        options={[
                          { key: "empty", label: "", value: undefined },
                          ...(regimes ?? []).map((element) => ({
                            key: element.id,
                            value: element.tideId ?? undefined,
                            label: element.name ?? "",
                          })),
                        ]}
                      />
                    ) : null}
                  </div>
                  <div className="form__row">
                    <LabeledTextArea
                      name="message"
                      label={t("MESSAGE")}
                      hasError={hasVisibleError("message")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.message}
                    />
                  </div>
                </div>
                <div className="form__region">
                  <div className="form__row">
                    <LabeledCheckbox
                      name="flightIncluded"
                      hasError={hasVisibleError("flightIncluded")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      defaultChecked={formik.values.flightIncluded}
                    >
                      <span>{t("WITH_FLIGHT")}</span>
                    </LabeledCheckbox>
                  </div>
                  <div className="form__row">
                    <LabeledCheckbox
                      name="newsletter"
                      hasError={hasVisibleError("newsletter")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      defaultChecked={formik.values.newsletter}
                    >
                      <span>{t("NEWS_LETTER_DESCRIPTION")}</span>
                    </LabeledCheckbox>
                  </div>
                  <div className="form__row">
                    <LabeledCheckbox
                      name="acceptTerms"
                      hasError={hasVisibleError("acceptTerms")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      defaultChecked={formik.values.acceptTerms}
                    >
                      {t("FORMS_PRIVACY_TEXT")}{" "}
                      <a
                        href={`/${privacyPath}`}
                        title={t("READ_THE_DISCLAIMER")}
                        target="_blank"
                        className="link"
                      >
                        {privacyText}
                      </a>
                    </LabeledCheckbox>
                  </div>
                </div>
                {Object.keys(flatErrors).filter((key) => get(formik.touched, key, false)).length >
                  0 && (
                  <div className="form__region form__region--errors">
                    <div className="form__row">
                      <div className="form__group">
                        <p className="form__error-heading">{t("INVALID_FIELDS")}</p>
                        <ul className="list">
                          {Object.keys(flatErrors)
                            .filter((key) => get(formik.touched, key, false))
                            .map((key) => (
                              <li key={key} data-key={key}>
                                {get(flatErrors, key)}
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {submitFailed && (
                  <div className="form__region form__region--errors">
                    <div className="form__row">
                      <div className="form__group">
                        <p className="form__error-heading">{t("SUBMIT_FAILED")}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="form__region">
                  <div className="form__row form__row--actions">
                    <div className="form__group">
                      <button
                        type="submit"
                        className="cta"
                        title={t("REQUEST_OFFER")}
                        disabled={submitting}
                      >
                        {submitting ? t("SENDING") : t("REQUEST_OFFER")}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          ) : (
            <div className="form-state form-state--success">
              <div className="contact-form__intro">
                <h2 className="contact-form__title">{confirmationTitle}</h2>
              </div>
              <form className="form" name="form--contact-succes" id="form--contact-succes">
                <div className="form__region">
                  <div className="form__row">
                    <div
                      className="form__group form__group--success-message"
                      dangerouslySetInnerHTML={{ __html: confirmationText ?? "" }}
                    ></div>
                  </div>
                </div>

                <div className="form__region">
                  <div className="form__row form__row--actions">
                    <div className="form__group">
                      <button
                        type="button"
                        className="cta"
                        title={t("REQUEST_NEW_OFFER")}
                        onClick={openForm}
                      >
                        {t("REQUEST_NEW_OFFER")}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default OfferForm;
